export interface TTSOptions {
  rate?: number; // Speech rate (0.1 to 10, default 1)
  pitch?: number; // Speech pitch (0 to 2, default 1)
  volume?: number; // Speech volume (0 to 1, default 1)
}

export interface TTSVoiceInfo {
  voice: SpeechSynthesisVoice;
  quality: 'high' | 'medium' | 'low';
  region: 'CN' | 'TW' | 'HK' | 'unknown';
}

class ChineseTTS {
  private synthesis: SpeechSynthesis | null = null;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private voices: SpeechSynthesisVoice[] = [];
  private bestVoice: SpeechSynthesisVoice | null = null;
  private isInitialized = false;

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synthesis = window.speechSynthesis;
      this.initializeVoices();
    }
  }

  /**
   * Check if the browser supports Speech Synthesis
   */
  isSupported(): boolean {
    return !!(typeof window !== 'undefined' && 'speechSynthesis' in window);
  }

  /**
   * Initialize and load available voices
   */
  private async initializeVoices(): Promise<void> {
    if (!this.synthesis) return;

    // Wait for voices to be loaded
    return new Promise((resolve) => {
      const loadVoices = () => {
        this.voices = this.synthesis!.getVoices();
        this.bestVoice = this.findBestChineseVoice();
        this.isInitialized = true;

        // Enhanced logging for debugging Android issues
        const chineseVoices = this.getChineseVoices();
        console.log(`🔊 TTS initialized with ${this.voices.length} total voices, ${chineseVoices.length} Chinese voices found`);
        console.log('Available voices:', this.voices.map(v => ({ name: v.name, lang: v.lang })));
        console.log('Best Chinese voice:', this.bestVoice?.name || 'None');

        resolve();
      };

      const attemptLoad = () => {
        if (this.synthesis!.getVoices().length > 0) {
          loadVoices();
        } else {
          // Retry after short delay for Android devices
          setTimeout(() => {
            if (this.synthesis!.getVoices().length > 0) {
              loadVoices();
            } else {
              console.warn('⚠️ No voices loaded after retry, proceeding anyway');
              loadVoices();
            }
          }, 500);
        }
      };

      // Voices might be loaded immediately or asynchronously
      if (this.synthesis!.getVoices().length > 0) {
        loadVoices();
      } else {
        this.synthesis!.addEventListener('voiceschanged', loadVoices, { once: true });
        // Increased timeout for slower Android devices
        setTimeout(attemptLoad, 3000);
      }
    });
  }

  /**
   * Find the best available Chinese voice
   */
  private findBestChineseVoice(): SpeechSynthesisVoice | null {
    const chineseVoices = this.getChineseVoices();

    if (chineseVoices.length === 0) {
      console.warn('⚠️ No Chinese voices available for TTS');
      return null;
    }

    // Expanded priorities for Android compatibility
    const priorities = [
      // Standard formats
      'zh-CN', 'zh-cn', 'zh-TW', 'zh-tw', 'zh',
      // Android/Google formats
      'cmn-Hans-CN', 'cmn-hans-cn', 'cmn-Hant-TW', 'cmn-hant-tw',
      // Underscore formats
      'zh_CN', 'zh_cn', 'zh_TW', 'zh_tw'
    ];

    // First try exact language code matches
    for (const priority of priorities) {
      const voice = chineseVoices.find(v =>
        v.voice.lang.toLowerCase() === priority.toLowerCase() ||
        v.voice.lang.toLowerCase().startsWith(priority.toLowerCase() + '-')
      );
      if (voice) {
        console.log(`✅ Selected Chinese voice: ${voice.voice.name} (${voice.voice.lang})`);
        return voice.voice;
      }
    }

    // Then try voice name matching for Android
    const namePatterns = ['chinese', 'mandarin', '中文'];
    for (const pattern of namePatterns) {
      const voice = chineseVoices.find(v =>
        v.voice.name.toLowerCase().includes(pattern)
      );
      if (voice) {
        console.log(`✅ Selected Chinese voice by name: ${voice.voice.name} (${voice.voice.lang})`);
        return voice.voice;
      }
    }

    // Fallback to first Chinese voice
    const fallbackVoice = chineseVoices[0];
    console.log(`🔄 Using fallback Chinese voice: ${fallbackVoice.voice.name} (${fallbackVoice.voice.lang})`);
    return fallbackVoice.voice;
  }

  /**
   * Get all available Chinese voices with quality assessment
   */
  getChineseVoices(): TTSVoiceInfo[] {
    const chineseVoices = this.voices.filter(voice => {
      const lang = voice.lang.toLowerCase();
      const name = voice.name.toLowerCase();

      // Standard patterns
      if (lang.startsWith('zh')) return true;

      // Android-specific patterns
      if (lang.startsWith('cmn')) return true; // Mandarin Chinese
      if (lang.includes('zh_')) return true; // zh_CN, zh_TW format

      // Voice name patterns for Android
      if (name.includes('chinese')) return true;
      if (name.includes('中文')) return true;
      if (name.includes('mandarin')) return true;
      if (name.includes('cantonese')) return true;

      return false;
    });

    return chineseVoices.map(voice => ({
      voice,
      quality: this.assessVoiceQuality(voice),
      region: this.detectRegion(voice.lang)
    }));
  }

  /**
   * Assess voice quality based on name and characteristics
   */
  private assessVoiceQuality(voice: SpeechSynthesisVoice): 'high' | 'medium' | 'low' {
    const name = voice.name.toLowerCase();

    // High quality indicators
    if (name.includes('neural') || name.includes('premium') || name.includes('natural')) {
      return 'high';
    }

    // Platform-specific quality assessment
    if (name.includes('microsoft') || name.includes('google') || name.includes('apple')) {
      return 'medium';
    }

    return 'low';
  }

  /**
   * Detect Chinese region from language code
   */
  private detectRegion(lang: string): 'CN' | 'TW' | 'HK' | 'unknown' {
    const lower = lang.toLowerCase();

    // Mainland China patterns
    if (lower.includes('cn') || lower === 'zh' || lower.includes('hans')) return 'CN';

    // Taiwan patterns
    if (lower.includes('tw') || lower.includes('hant')) return 'TW';

    // Hong Kong patterns
    if (lower.includes('hk')) return 'HK';

    return 'unknown';
  }

  /**
   * Check if Chinese TTS is available
   */
  async isChineseAvailable(): Promise<boolean> {
    if (!this.isSupported()) return false;

    if (!this.isInitialized) {
      await this.initializeVoices();
    }

    return this.bestVoice !== null;
  }

  /**
   * Speak Chinese text
   */
  async speak(text: string, options: TTSOptions = {}): Promise<void> {
    if (!this.synthesis) {
      throw new Error('Speech synthesis not supported');
    }

    if (!this.isInitialized) {
      await this.initializeVoices();
    }

    if (!this.bestVoice) {
      throw new Error('No Chinese voice available');
    }

    // Stop any current speech
    this.stop();

    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text);

      // Configure voice and options
      utterance.voice = this.bestVoice;
      utterance.rate = options.rate ?? 0.9; // Slightly slower for better pronunciation
      utterance.pitch = options.pitch ?? 1;
      utterance.volume = options.volume ?? 1;
      utterance.lang = this.bestVoice!.lang;

      // Event handlers
      utterance.onstart = () => {
        console.log(`🔊 Started speaking: "${text}"`);
      };

      utterance.onend = () => {
        console.log(`✅ Finished speaking: "${text}"`);
        this.currentUtterance = null;
        resolve();
      };

      utterance.onerror = (event) => {
        console.error('🚫 TTS error:', event.error);
        this.currentUtterance = null;
        reject(new Error(`Speech synthesis failed: ${event.error}`));
      };

      // Store reference and speak
      this.currentUtterance = utterance;
      this.synthesis!.speak(utterance);
    });
  }

  /**
   * Stop current speech
   */
  stop(): void {
    if (this.synthesis) {
      this.synthesis.cancel();
      this.currentUtterance = null;
    }
  }

  /**
   * Check if currently speaking
   */
  isSpeaking(): boolean {
    return this.synthesis ? this.synthesis.speaking : false;
  }

  /**
   * Pause current speech
   */
  pause(): void {
    if (this.synthesis && this.synthesis.speaking) {
      this.synthesis.pause();
    }
  }

  /**
   * Resume paused speech
   */
  resume(): void {
    if (this.synthesis && this.synthesis.paused) {
      this.synthesis.resume();
    }
  }

  /**
   * Get current voice information
   */
  getCurrentVoice(): TTSVoiceInfo | null {
    if (!this.bestVoice) return null;

    return {
      voice: this.bestVoice,
      quality: this.assessVoiceQuality(this.bestVoice),
      region: this.detectRegion(this.bestVoice.lang)
    };
  }
}

// Export singleton instance
export const chineseTTS = new ChineseTTS();

// Also export the class for type checking if needed
export { ChineseTTS };

// Helper function for easy usage
export async function speakChinese(text: string, options?: TTSOptions): Promise<boolean> {
  try {
    if (!(await chineseTTS.isChineseAvailable())) {
      console.warn('Chinese TTS not available');
      return false;
    }

    await chineseTTS.speak(text, options);
    return true;
  } catch (error) {
    console.error('Failed to speak Chinese text:', error);
    return false;
  }
}

export default chineseTTS;